import { useEffect, useRef, useState } from "react";

export const useCameraCheck = () => {
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<string | null>(null);
  const [isCameraReady, setIsCameraReady] = useState<boolean>(false);
  const [zoom, setZoom] = useState<number>(100);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoStream = useRef<MediaStream | null>(null);

  useEffect(() => {
    const getCameras = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter((device) => device.kind === "videoinput");
        setCameras(videoDevices);

        const storedCamera = localStorage.getItem("selectedCamera");
        if (storedCamera) {
          setSelectedCamera(storedCamera);
        }

        const storedZoom = localStorage.getItem("cameraZoom");
        if (storedZoom) {
          const zoomValue = parseInt(storedZoom, 10);
          if (!Number.isNaN(zoomValue) && zoomValue >= 100 && zoomValue <= 300) {
            setZoom(zoomValue);
          }
        }
      } catch (error) {
        setCameras([]);
      }
    };

    getCameras();
  }, []);

  useEffect(() => {
    if (selectedCamera) {
      const requestCameraAccess = async () => {
        setIsCameraReady(false);

        try {
          let constraints;
          if (selectedCamera) {
            constraints = {
              video: { deviceId: { exact: selectedCamera } },
            };
          } else {
            constraints = {
              video: true,
            };
          }

          const stream = await navigator.mediaDevices.getUserMedia(constraints);

          videoStream.current = stream;

          setIsCameraReady(true);

          setTimeout(() => {
            stream.getTracks().forEach((track) => track.stop());
          }, 100);
        } catch (error) {
          setIsCameraReady(false);
        }
      };

      requestCameraAccess();

      return () => {
        setIsCameraReady(false);
        if (videoStream.current) {
          videoStream.current.getTracks().forEach((track) => track.stop());
          videoStream.current = null;
        }
      };
    } else {
      setIsCameraReady(false);
    }
  }, [selectedCamera]);

  const handleCameraChange = (value: string) => {
    setIsCameraReady(false);
    setSelectedCamera(value || null);

    if (value) {
      localStorage.setItem("selectedCamera", value);
    } else {
      localStorage.removeItem("selectedCamera");
    }
  };

  const handleZoomChange = (newZoom: number) => {
    setZoom(newZoom);
    localStorage.setItem("cameraZoom", newZoom.toString());
  };

  useEffect(() => {
    return () => {
      if (videoStream.current) {
        videoStream.current.getTracks().forEach((track) => {
          track.stop();
        });
        videoStream.current = null;
      }
    };
  }, []);

  return {
    cameras,
    selectedCamera,
    isCameraReady,
    zoom,
    videoRef,
    handleCameraChange,
    handleZoomChange,
  };
};
